import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearNationalityDetails } from "./nationalitySlice";
import {
  createNationality,
  getAllNationalities,
  getNationalityByID,
  updateNationality,
} from "./services/nationality.services";
import { closed } from "../../components/drawer/drawerSlice";

const CreateNationality: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const NationalityDetail = useAppSelector(
    (state) => state.nationality.NationalityDetail
  );
  const NationalityID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.nationality.formLoading);

  useEffect(() => {
    if (NationalityID) {
      dispatch(getNationalityByID(NationalityID));
    }
  }, [NationalityID]);

  useEffect(() => {
    return () => {
      dispatch(clearNationalityDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!NationalityID) {
        const insertPayload = {
          Nationality: values.Nationality,
          IsActive: true,
        };
        const response = await dispatch(createNationality(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearNationalityDetails());
          dispatch(getAllNationalities());
        }
      } else {
        const payload = {
          ID: NationalityID,
          Nationality: values.Nationality,
          IsActive: values.IsActive,
        };
        const response = await dispatch(updateNationality(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearNationalityDetails());
          dispatch(getAllNationalities());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={NationalityDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Nationality"
                label="Nationality"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label={NationalityID ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateNationality;
